import { Link } from 'react-router-dom';
import { ArrowUpDown, Eye, Check, X, Mail, Calendar } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import type { AdmissionInquiryRow } from './types';

export interface InquiryColumnHandlers {
  schoolSlug: string | undefined;
  onView?: (inquiry: AdmissionInquiryRow) => void;
  onUpdateState?: (inquiry: AdmissionInquiryRow, state: string) => void;
  onAccept?: (inquiry: AdmissionInquiryRow) => void;
}

const STATE_BADGES: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  SUBMITTED: { label: 'Submitted', variant: 'outline' },
  EMAILED: { label: 'Emailed', variant: 'secondary' },
  INTERVIEW_SCHEDULED: { label: 'Interview', variant: 'default' },
  ACCEPTED: { label: 'Accepted', variant: 'default' },
  DENIED: { label: 'Denied', variant: 'destructive' },
};

export const getInquiryColumns = ({ schoolSlug, onView, onUpdateState, onAccept }: InquiryColumnHandlers): ColumnDef<AdmissionInquiryRow>[] => [
  {
    id: 'name',
    accessorFn: (row) => `${row.parentFirstName} ${row.parentLastName}`,
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Name
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const familySlug = row.original.family?.slug || row.original.familyEnrollment?.family?.slug;
      const name = `${row.original.parentFirstName} ${row.original.parentLastName}`;
      if (familySlug && schoolSlug) {
        return (
          <Link
            to={`/app/${schoolSlug}/families/${familySlug}`}
            className="font-medium text-primary hover:underline"
          >
            {name}
          </Link>
        );
      }
      return <span className="font-medium">{name}</span>;
    },
  },
  {
    accessorKey: 'parentEmail',
    header: 'Email',
    cell: ({ row }) => (
      <span className="text-muted-foreground">{row.original.parentEmail}</span>
    ),
  },
  {
    accessorKey: 'numStudents',
    header: 'Students',
    cell: ({ row }) => (
      <div className="text-center">{row.original.numStudents}</div>
    ),
  },
  {
    accessorKey: 'state',
    header: 'Status',
    cell: ({ row }) => {
      const state = row.original.state as string;
      const badge = STATE_BADGES[state] || { label: state, variant: 'outline' as const };
      return <Badge variant={badge.variant}>{badge.label}</Badge>;
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Date
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const date = new Date(row.original.createdAt as string);
      return <div>{date.toLocaleDateString()}</div>;
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const inquiry = row.original;
      const state = inquiry.state as string;
      const isClosed = state === 'ACCEPTED' || state === 'DENIED';

      return (
        <div className="flex items-center gap-1 justify-end">
          {onView && (
            <Button variant="ghost" size="sm" onClick={() => onView(inquiry)} title="View inquiry">
              <Eye className="h-4 w-4" />
            </Button>
          )}
          {!isClosed && onUpdateState && (
            <>
              {state === 'SUBMITTED' && (
                <Button variant="ghost" size="sm" onClick={() => onUpdateState(inquiry, 'EMAILED')} title="Mark as emailed">
                  <Mail className="h-4 w-4" />
                </Button>
              )}
              {(state === 'SUBMITTED' || state === 'EMAILED') && (
                <Button variant="ghost" size="sm" onClick={() => onUpdateState(inquiry, 'INTERVIEW_SCHEDULED')} title="Schedule interview">
                  <Calendar className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
          {!isClosed && onAccept && (
            <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700" onClick={() => onAccept(inquiry)} title="Accept">
              <Check className="h-4 w-4" />
            </Button>
          )}
          {!isClosed && onUpdateState && (
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => onUpdateState(inquiry, 'DENIED')} title="Deny">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      );
    },
  },
];
